/** Manages stamina drain during running and delayed recharge when not draining. */
import Phaser from 'phaser';
import { Component } from './Component';
import { ConfigLoader } from '../systems/ConfigLoader';
import { DebugConfig } from '../debug/DebugConfig';
import { DebugLog } from '../debug/DebugLog';

/** Tracks stamina, applies per-second drain while running, and recharges after a configurable delay. */
export class StaminaComponent implements Component {
  current: number;
  private baseMax: number;
  private drainRate: number;
  private rechargeRate: number;
  private rechargeDelay: number;
  private timeSinceDrain: number = 0;
  private _isDraining: boolean = false;
  private _isStationary: boolean = false;
  private events: Phaser.Events.EventEmitter;

  /** Fraction of max stamina refunded when a dash-attack lands (0 = disabled, 0.3 = 30%). Set by Adrenaline. */
  dashAttackStaminaRefundRatio: number = 0;

  /** Multiplier applied to stamina drain rate (1.0 = normal, 0.5 = half drain). Set by Tireless. */
  drainMultiplier: number = 1.0;

  /** Multiplier applied to stamina recharge rate (1.0 = normal, 2.0 = double recharge). Set by Quick Recovery. */
  rechargeMultiplier: number = 1.0;

  /** Recharge rate multiplier while walking (default 0.5 = half speed). Set by Steady Pace to 1.0 for full-speed recharge while moving. */
  walkingRechargeRate: number = 0.5;

  /** Fractional bonus to max stamina (0 = no bonus, 0.3 = +30%). Set by Deep Reserves. */
  private _staminaMaxBonus: number = 0;

  constructor(events: Phaser.Events.EventEmitter) {
    const config = ConfigLoader.movement.run;
    this.baseMax = config.staminaMax;
    this.current = this.max;
    this.drainRate = config.staminaDrainPerSecond;
    this.rechargeRate = config.staminaRechargePerSecond;
    this.rechargeDelay = config.staminaRechargeDelay;
    this.events = events;
  }

  /** Effective max stamina including bonus. */
  get max(): number {
    return this.baseMax * (1 + this._staminaMaxBonus);
  }

  /** Fractional bonus to max stamina (0 = no bonus, 0.3 = +30%). Set by Deep Reserves. */
  get staminaMaxBonus(): number {
    return this._staminaMaxBonus;
  }

  set staminaMaxBonus(value: number) {
    this._staminaMaxBonus = value;
    // Clamp current to new max
    if (this.current > this.max) {
      this.current = this.max;
    }
    this.events.emit('stamina-changed', this.current, this.max);
  }

  get isFull(): boolean {
    return this.current >= this.max;
  }

  get isEmpty(): boolean {
    return this.current <= 0;
  }

  get ratio(): number {
    return this.current / this.max;
  }

  /** Signal that stamina should drain this frame (call each frame while running). */
  drain(): void {
    this._isDraining = true;
    this.timeSinceDrain = 0;
  }

  /** Signal that stamina should stop draining and begin the recharge delay countdown. */
  stopDraining(): void {
    this._isDraining = false;
  }

  /** Immediately deduct a one-shot stamina cost (e.g. skill activation). Floors at 0.
   *  Does not affect the drain/recharge state — distinct from the running-drain path.
   *  No-op when DebugConfig.flags.infiniteStamina is true. */
  spend(amount: number): void {
    if (DebugConfig.flags.infiniteStamina) return;
    this.current = Math.max(0, this.current - amount);
    this.events.emit('stamina-changed', this.current, this.max);
  }

  /** Suppress stamina recharge for the current frame by resetting the recharge delay timer.
   *  Call this each frame while an attack window is active — recharge resumes naturally
   *  one frame after the last call, since timeSinceDrain will start accumulating again. */
  blockRecharge(): void {
    this.timeSinceDrain = 0;
  }

  /** Refund stamina for a dash-attack hit. No-op if dashAttackStaminaRefundRatio is 0.
   *  Trigger: call from CombatHitResolver when attackType === 'dash_attack' and a hit lands. */
  applyDashAttackRefund(): void {
    if (this.dashAttackStaminaRefundRatio <= 0) return;
    const refund = this.max * this.dashAttackStaminaRefundRatio;
    this.current = Math.min(this.max, this.current + refund);
    this.events.emit('stamina-changed', this.current, this.max);
  }

  /** Signal whether the player is stationary this frame (doubles recharge rate when true). */
  setStationary(value: boolean): void {
    this._isStationary = value;
  }

  update(delta: number): void {
    const dt = delta / 1000;
    const wasEmpty = this.current <= 0;

    if (this._isDraining) {
      if (!DebugConfig.flags.infiniteStamina) {
        this.current = Math.max(0, this.current - this.drainRate * this.drainMultiplier * dt);
      }
      this.timeSinceDrain = 0;
      if (this.current <= 0) {
        this._isDraining = false;
        DebugLog.log('physics', 'STAMINA depleted — recharge delay starts');
      }
    } else {
      this.timeSinceDrain += delta;
      if (this.timeSinceDrain >= this.rechargeDelay && this.current < this.max) {
        if (wasEmpty && this.current <= 0) {
          DebugLog.log('physics', `STAMINA recharge begins after ${this.rechargeDelay}ms delay`);
        }
        const rate = this.rechargeRate * this.rechargeMultiplier * (this._isStationary ? 2 : this.walkingRechargeRate);
        this.current = Math.min(this.max, this.current + rate * dt);
      } else if (wasEmpty && this.timeSinceDrain < this.rechargeDelay) {
        DebugLog.throttled('stamina-delay', 200, 'physics',
          `STAMINA waiting for recharge delay: ${this.timeSinceDrain.toFixed(0)}/${this.rechargeDelay}ms`);
      }
    }

    this.events.emit('stamina-changed', this.current, this.max);
  }

  destroy(): void {
    // Nothing to clean up
  }
}
